import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PERSONA_IMAGES, voiceChat, textToSpeech, testVoiceChat } from "@/lib/api";
import {
  Mic, Square, Send, Play, Pause, RotateCcw, FileText,
  Loader2, WifiOff, Keyboard, Volume2,
  FlaskConical, Sparkles, Meh, ThumbsDown, TrendingUp, TrendingDown,
  Info, CheckCircle, AlertTriangle, Building2, Zap, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";

const PERSONA_INFO = {
  jamie_sterling: { name: "Jamie Sterling", role: "Managing Director", short: "JS" },
  evelyn_reed: { name: "Dr. Evelyn Reed", role: "Operating Partner", short: "ER" },
  ben_carter: { name: "Ben Carter", role: "VP Procurement", short: "BC" },
};

const PERSONA_DETAILS = {
  jamie_sterling: {
    archetype: "Skeptic / Economic Buyer",
    difficulty: "Hard",
    focus_areas: ["Metrics (M)", "Economic Buyer (E)"],
    background: "Former investment banker with deep expertise in M&A and financial modeling. Jamie is a deal-maker, not an operator, which is why Innovatech's operational challenges are outside his comfort zone.",
    approach: "Expect a fast-paced, direct conversation. Jamie will cut through fluff and demand quantified ROI, proven track records, and fast timelines.",
    positive_triggers: ["Quantified ROI", "Fast timeline", "Proven track record"],
    negative_triggers: ["Vague promises", "Consultant jargon", "Long explanations"]
  },
  evelyn_reed: {
    archetype: "Champion / Decision Maker",
    difficulty: "Medium",
    focus_areas: ["Decision Criteria (D1)", "Champion (C1)"],
    background: "PhD in Industrial Engineering and former COO of a major manufacturing conglomerate. Evelyn was brought into Helios to provide operational oversight.",
    approach: "A methodical conversation focused on process and evidence. Evelyn will ask detailed 'how' questions about your vetting methodology and risk mitigation.",
    positive_triggers: ["Detailed methodology", "Evidence & data", "Acknowledging complexity"],
    negative_triggers: ["Oversimplification", "Ignoring her expertise", "Lack of due diligence"]
  },
  ben_carter: {
    archetype: "Blocker / Gatekeeper",
    difficulty: "Very Hard",
    focus_areas: ["Paper Process (P)", "Competition (C2)"],
    background: "Career procurement professional focused on cost reduction, vendor standardization, and contract compliance. Ben views specialized networks as overpriced.",
    approach: "A challenging conversation centered on price, terms, and process. Ben will try to commoditize your service and push for discounts.",
    positive_triggers: ["Clear pricing", "Contract flexibility", "Risk mitigation"],
    negative_triggers: ["Unclear costs", "Rigid terms", "Dismissing procurement"]
  }
};

const SCENARIO_SUMMARY = {
  client: "Helios Capital Partners",
  clientDesc: "$2B Mid-Market Private Equity Fund",
  problem: "Their acquisition of Innovatech Dynamics is failing its 100-day plan. Supply chain chaos from a botched SensorTech integration is eroding margins by 8%.",
  yourRole: "Senior Partner at Apex Executive Network (AEN). Position AEN as the only logical solution. You are their peer, not a vendor.",
  keyPoints: [
    "Lead with execution, not advice -- operators who do, not slide decks",
    "Speed to impact -- weeks, not 6-9 months like consultants",
    "De-risk the investment -- proven track records in similar turnarounds",
  ]
};

export const VoiceChatPanel = ({ dealState, messages, onVoiceChatResponse, onSwitchToText }) => {
  const [voiceState, setVoiceState] = useState("idle");
  const [audioBlob, setAudioBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [isPersonaSpeaking, setIsPersonaSpeaking] = useState(false);
  const [isPersonaPaused, setIsPersonaPaused] = useState(false);
  const [lagWarning, setLagWarning] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [lastResponseAudio, setLastResponseAudio] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [testModeOpen, setTestModeOpen] = useState(false);
  const [loadingTest, setLoadingTest] = useState(null);
  const [briefingOpen, setBriefingOpen] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const previewAudioRef = useRef(null);
  const personaAudioRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const sendTimestampRef = useRef(null);

  const personaId = dealState?.active_persona || "jamie_sterling";
  const persona = PERSONA_INFO[personaId] || PERSONA_INFO.jamie_sterling;

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (previewAudioRef.current) previewAudioRef.current.pause();
      if (personaAudioRef.current) personaAudioRef.current.pause();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      clearInterval(recordingTimerRef.current);
    };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
        setVoiceState("recorded");
      };

      mediaRecorder.start(100);
      setVoiceState("recording");
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch (err) {
      toast.error("Microphone access denied. Please allow microphone permissions.");
    }
  }, [previewUrl]);

  const stopRecording = useCallback(() => {
    clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const playPreview = useCallback(() => {
    if (!previewUrl) return;
    if (previewAudioRef.current) previewAudioRef.current.pause();
    const audio = new Audio(previewUrl);
    previewAudioRef.current = audio;
    audio.onended = () => setIsPreviewPlaying(false);
    audio.play();
    setIsPreviewPlaying(true);
  }, [previewUrl]);

  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
    setIsPreviewPlaying(false);
  }, []);

  const reRecord = useCallback(() => {
    if (previewAudioRef.current) previewAudioRef.current.pause();
    setAudioBlob(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setIsPreviewPlaying(false);
    setVoiceState("idle");
  }, [previewUrl]);

  const sendVoice = useCallback(async () => {
    if (!audioBlob || !dealState?.session_id) return;
    setVoiceState("sending");
    setLagWarning(null);
    sendTimestampRef.current = Date.now();

    const lagTimer = setTimeout(() => setLagWarning("slow"), 5000);
    const lagTimer2 = setTimeout(() => setLagWarning("very_slow"), 10000);

    try {
      const response = await voiceChat(dealState.session_id, audioBlob);
      clearTimeout(lagTimer);
      clearTimeout(lagTimer2);
      const elapsed = Date.now() - sendTimestampRef.current;
      setLagWarning(elapsed > 8000 ? "slow" : null);

      const data = response.data;
      onVoiceChatResponse(data);

      if (data.audio_base64) {
        playPersonaAudio(data.audio_base64);
        setLastResponseAudio(data.audio_base64);
      } else {
        setVoiceState("idle");
      }

      setAudioBlob(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    } catch (error) {
      clearTimeout(lagTimer);
      clearTimeout(lagTimer2);
      console.error("Voice chat error:", error);
      toast.error("Failed to process voice message. Try again or switch to text.");
      setVoiceState("recorded");
    }
  }, [audioBlob, dealState?.session_id, onVoiceChatResponse, previewUrl]);

  const playPersonaAudio = useCallback((base64) => {
    if (personaAudioRef.current) personaAudioRef.current.pause();
    const audio = new Audio(`data:audio/mp3;base64,${base64}`);
    personaAudioRef.current = audio;
    setIsPersonaSpeaking(true);
    setIsPersonaPaused(false);
    setVoiceState("playing");

    audio.onended = () => {
      setIsPersonaSpeaking(false);
      setIsPersonaPaused(false);
      setVoiceState("idle");
    };
    audio.play();
  }, []);

  const pausePersona = useCallback(() => {
    if (personaAudioRef.current && !personaAudioRef.current.paused) {
      personaAudioRef.current.pause();
      setIsPersonaPaused(true);
      setIsPersonaSpeaking(false);
    }
  }, []);

  const resumePersona = useCallback(() => {
    if (personaAudioRef.current && personaAudioRef.current.paused) {
      personaAudioRef.current.play();
      setIsPersonaPaused(false);
      setIsPersonaSpeaking(true);
    }
  }, []);

  const replayPersona = useCallback(() => {
    if (lastResponseAudio) {
      playPersonaAudio(lastResponseAudio);
    }
  }, [lastResponseAudio, playPersonaAudio]);

  const handleTestAutoSend = useCallback(async (quality) => {
    if (!dealState?.session_id) return;
    setLoadingTest(quality);
    setTestModeOpen(false);
    setVoiceState("sending");
    setLagWarning(null);
    const startTime = Date.now();

    const lagTimer = setTimeout(() => setLagWarning("slow"), 5000);
    const lagTimer2 = setTimeout(() => setLagWarning("very_slow"), 10000);

    try {
      const response = await testVoiceChat(dealState.session_id, quality);
      clearTimeout(lagTimer);
      clearTimeout(lagTimer2);
      const elapsed = Date.now() - startTime;
      setLagWarning(elapsed > 8000 ? "slow" : null);

      const data = response.data;
      onVoiceChatResponse(data);

      if (data.audio_base64) {
        setLastResponseAudio(data.audio_base64);
        playPersonaAudio(data.audio_base64);
      } else {
        setVoiceState("idle");
      }
      setTimeout(() => setTranscriptOpen(true), 300);
    } catch (error) {
      clearTimeout(lagTimer);
      clearTimeout(lagTimer2);
      console.error("Test auto-send error:", error);
      toast.error("Failed to generate and send test response.");
      setVoiceState("idle");
    } finally {
      setLoadingTest(null);
    }
  }, [dealState?.session_id, onVoiceChatResponse, playPersonaAudio]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const isActive = dealState?.is_active;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Transcript Sheet */}
      <Sheet open={transcriptOpen} onOpenChange={setTranscriptOpen}>
        <SheetContent side="right" className="w-[400px] sm:w-[500px]" aria-describedby="transcript-description">
          <SheetHeader>
            <SheetTitle className="text-primary">Conversation Transcript</SheetTitle>
            <p id="transcript-description" className="text-xs text-slate-400">Full text history of your conversation.</p>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-80px)] mt-4 pr-4">
            <div className="space-y-3">
              {messages.filter((m) => m.role !== "system").map((msg, idx) => (
                <div key={idx} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                  <Avatar className="w-7 h-7 flex-shrink-0">
                    {msg.role === "user" ? (
                      <AvatarFallback className="bg-primary text-white text-[10px]">You</AvatarFallback>
                    ) : (
                      <>
                        <AvatarImage src={PERSONA_IMAGES[msg.persona_id]} />
                        <AvatarFallback className="bg-slate-100 text-[10px]">
                          {PERSONA_INFO[msg.persona_id]?.short || "AI"}
                        </AvatarFallback>
                      </>
                    )}
                  </Avatar>
                  <div className="flex flex-col max-w-[80%]">
                    {msg.role !== "user" && msg.trust_delta !== undefined && msg.trust_delta !== 0 && (
                      <div className="mb-1">
                        {msg.trust_delta > 0 ? (
                          <Badge variant="outline" className="text-[10px] py-0 h-4 bg-emerald-50 text-emerald-600 border-emerald-200">
                            <TrendingUp className="w-2.5 h-2.5 mr-0.5" />+{msg.trust_delta}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] py-0 h-4 bg-red-50 text-red-600 border-red-200">
                            <TrendingDown className="w-2.5 h-2.5 mr-0.5" />{msg.trust_delta}
                          </Badge>
                        )}
                      </div>
                    )}
                    <div className={`rounded-xl px-3 py-2 text-sm ${
                      msg.role === "user"
                        ? "bg-primary text-white"
                        : "bg-slate-50 text-slate-700 border border-slate-200"
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Briefing Sheet */}
      <Sheet open={briefingOpen} onOpenChange={setBriefingOpen}>
        <SheetContent side="left" className="w-[400px] sm:w-[500px]" aria-describedby="briefing-description">
          <SheetHeader>
            <SheetTitle className="text-primary">Scenario Briefing</SheetTitle>
            <p id="briefing-description" className="text-xs text-slate-400">Scenario and persona reference.</p>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-80px)] mt-4 pr-4">
            <div className="space-y-4">
              {/* Scenario Section */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{SCENARIO_SUMMARY.client}</span>
                </div>
                <p className="text-xs text-slate-400 mb-2">{SCENARIO_SUMMARY.clientDesc}</p>
                <div className="p-3 bg-orange-50/80 rounded-lg border border-orange-100 mb-2">
                  <p className="text-xs font-medium text-orange-700 mb-1">The Problem</p>
                  <p className="text-xs text-slate-600">{SCENARIO_SUMMARY.problem}</p>
                </div>
                <div className="p-3 bg-blue-50/80 rounded-lg border border-blue-100 mb-2">
                  <p className="text-xs font-medium text-primary mb-1">Your Role</p>
                  <p className="text-xs text-slate-600">{SCENARIO_SUMMARY.yourRole}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-1">Key Talking Points</p>
                  <ul className="space-y-1">
                    {SCENARIO_SUMMARY.keyPoints.map((pt, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-slate-500">
                        <Zap className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />{pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="border-t border-slate-100 my-3"></div>

              {/* Persona Section */}
              {(() => {
                const pd = PERSONA_DETAILS[personaId] || PERSONA_DETAILS.jamie_sterling;
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={PERSONA_IMAGES[personaId]} />
                        <AvatarFallback className="text-[8px] bg-slate-100">{persona.short}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{persona.name}</span>
                      <Badge variant="outline" className="text-[10px] py-0 h-4 bg-white">{pd.difficulty}</Badge>
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs text-slate-400">{persona.role}</span>
                      <span className="text-xs text-slate-300">|</span>
                      <span className="text-xs text-slate-500">{pd.archetype}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {pd.focus_areas.map((area, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{area}</span>
                      ))}
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 mb-2">
                      <p className="text-xs font-medium text-slate-700 mb-1">Background</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{pd.background}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 mb-3">
                      <p className="text-xs font-medium text-slate-700 mb-1">What to Expect</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{pd.approach}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2.5 bg-emerald-50/50 rounded-lg border border-emerald-100">
                        <p className="text-[10px] font-semibold text-emerald-600 mb-1">Builds Trust</p>
                        <ul className="space-y-0.5">
                          {pd.positive_triggers.map((t, i) => (
                            <li key={i} className="text-[10px] text-slate-500 flex items-center gap-1">
                              <CheckCircle className="w-2.5 h-2.5 text-emerald-500" />{t}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-2.5 bg-red-50/50 rounded-lg border border-red-100">
                        <p className="text-[10px] font-semibold text-red-500 mb-1">Hurts Trust</p>
                        <ul className="space-y-0.5">
                          {pd.negative_triggers.map((t, i) => (
                            <li key={i} className="text-[10px] text-slate-500 flex items-center gap-1">
                              <AlertTriangle className="w-2.5 h-2.5 text-red-400" />{t}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Lag Warning */}
      {lagWarning && (
        <div className={`px-4 py-2 flex items-center gap-2 text-xs font-medium ${
          lagWarning === "very_slow"
            ? "bg-red-50 text-red-600 border-b border-red-100"
            : "bg-amber-50 text-amber-600 border-b border-amber-100"
        }`} data-testid="lag-warning">
          <WifiOff className="w-3.5 h-3.5" />
          {lagWarning === "very_slow"
            ? "Very slow connection. We recommend switching to Text Chat."
            : "Slow network detected. Response may take longer."}
        </div>
      )}

      {/* Persona Display */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="relative mb-6">
          <div className={`rounded-full transition-all duration-300 ${
            isPersonaSpeaking
              ? "ring-4 ring-primary/40 ring-offset-4 ring-offset-white shadow-lg shadow-primary/10"
              : isPersonaPaused
                ? "ring-4 ring-amber-300 ring-offset-4 ring-offset-white"
                : "ring-2 ring-slate-200 ring-offset-2 ring-offset-white"
          }`}>
            <Avatar className="w-28 h-28">
              <AvatarImage src={PERSONA_IMAGES[personaId]} className="object-cover" />
              <AvatarFallback className="text-2xl bg-slate-100 text-slate-500">
                {persona.short}
              </AvatarFallback>
            </Avatar>
          </div>

          {isPersonaSpeaking && (
            <>
              <div className="absolute inset-0 rounded-full animate-ping opacity-10 bg-primary" style={{ animationDuration: "1.5s" }} />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <Volume2 className="w-3.5 h-3.5 text-white" />
              </div>
            </>
          )}
          {isPersonaPaused && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
              <Pause className="w-3.5 h-3.5 text-white" />
            </div>
          )}
        </div>

        <h3 className="text-base font-semibold text-slate-900">{persona.name}</h3>
        <p className="text-sm text-slate-400 mb-6">{persona.role}</p>

        {(isPersonaSpeaking || isPersonaPaused) && (
          <div className="flex items-center gap-1 mb-4 h-8">
            {isPersonaSpeaking ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-primary rounded-full animate-voice-bar"
                  style={{ animationDelay: `${i * 0.15}s`, height: "100%" }}
                />
              ))
            ) : (
              <span className="text-xs text-amber-500 font-medium">Paused</span>
            )}
          </div>
        )}

        {(isPersonaSpeaking || isPersonaPaused || lastResponseAudio) && voiceState !== "sending" && (
          <div className="flex items-center gap-3" data-testid="persona-playback-controls">
            {isPersonaSpeaking ? (
              <Button variant="outline" size="sm" onClick={pausePersona} className="gap-1.5 border-slate-200" data-testid="pause-persona-btn">
                <Pause className="w-4 h-4" /> Pause
              </Button>
            ) : isPersonaPaused ? (
              <Button variant="outline" size="sm" onClick={resumePersona} className="gap-1.5 border-primary/30 text-primary" data-testid="play-persona-btn">
                <Play className="w-4 h-4" /> Resume
              </Button>
            ) : null}
            {!isPersonaSpeaking && lastResponseAudio && (
              <Button variant="outline" size="sm" onClick={replayPersona} className="gap-1.5 border-slate-200" data-testid="replay-persona-btn">
                <RotateCcw className="w-4 h-4" /> Replay
              </Button>
            )}
          </div>
        )}

        {voiceState === "sending" && (
          <div className="flex flex-col items-center gap-3 mt-4">
            <Loader2 className="w-7 h-7 text-primary animate-spin" />
            <p className="text-sm text-slate-400">Processing your message...</p>
          </div>
        )}
      </div>

      {/* Mic Controls */}
      <div className="flex-shrink-0 px-6 py-6 bg-white border-t border-slate-100">
        <div className="flex flex-col items-center gap-4">
          {voiceState === "idle" && isActive && (
            <div className="flex items-center gap-4">
              <Popover open={testModeOpen} onOpenChange={setTestModeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-12 h-12 rounded-full border-purple-200 hover:border-purple-300 hover:bg-purple-50 p-0"
                    disabled={loadingTest !== null}
                    data-testid="voice-test-mode-btn"
                  >
                    {loadingTest ? (
                      <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                    ) : (
                      <FlaskConical className="w-5 h-5 text-purple-500" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3" align="center" side="top">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                      <FlaskConical className="w-4 h-4 text-purple-500" />
                      <span className="font-semibold text-sm text-slate-800">Test Mode</span>
                      <Badge variant="outline" className="ml-auto text-xs bg-purple-50 text-purple-600 border-purple-200">
                        Auto-send
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400">Generates and sends a response:</p>
                    <div className="space-y-2">
                      {[
                        { quality: "good", label: "Good", color: "emerald", Icon: Sparkles },
                        { quality: "medium", label: "Medium", color: "amber", Icon: Meh },
                        { quality: "bad", label: "Bad", color: "red", Icon: ThumbsDown },
                      ].map(({ quality, label, color, Icon }) => (
                        <Button
                          key={quality}
                          type="button"
                          variant="outline"
                          className={`w-full justify-between gap-2 h-auto py-2.5 border-${color}-200 hover:bg-${color}-50 hover:border-${color}-300`}
                          onClick={() => handleTestAutoSend(quality)}
                          disabled={loadingTest !== null}
                          data-testid={`voice-test-${quality}-btn`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full bg-${color}-50 flex items-center justify-center flex-shrink-0`}>
                              <Icon className={`w-3.5 h-3.5 text-${color}-500`} />
                            </div>
                            <p className={`font-medium text-sm text-${color}-600`}>{label}</p>
                          </div>
                          <Send className={`w-4 h-4 text-${color}-400`} />
                        </Button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                onClick={startRecording}
                className="w-16 h-16 rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transition-transform hover:scale-105"
                data-testid="mic-btn"
              >
                <Mic className="w-7 h-7 text-white" />
              </Button>
            </div>
          )}

          {voiceState === "recording" && (
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-red-500">Recording {formatTime(recordingTime)}</span>
              </div>
              <Button
                onClick={stopRecording}
                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200"
                data-testid="stop-recording-btn"
              >
                <Square className="w-6 h-6 text-white" />
              </Button>
            </div>
          )}

          {voiceState === "recorded" && (
            <div className="flex items-center gap-3">
              <Button variant="outline" size="lg" onClick={isPreviewPlaying ? stopPreview : playPreview} className="gap-2 border-slate-200" data-testid="preview-play-btn">
                {isPreviewPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isPreviewPlaying ? "Stop" : "Play"}
              </Button>
              <Button variant="outline" size="lg" onClick={reRecord} className="gap-2 border-slate-200" data-testid="re-record-btn">
                <RotateCcw className="w-4 h-4" />
                Re-Record
              </Button>
              <Button size="lg" onClick={sendVoice} className="gap-2 bg-primary hover:bg-primary/90 px-6" data-testid="send-voice-btn">
                <Send className="w-4 h-4" />
                Send
              </Button>
            </div>
          )}

          {(voiceState === "sending" || voiceState === "playing") && (
            <Button disabled className="w-16 h-16 rounded-full bg-slate-100 cursor-not-allowed" data-testid="mic-btn-disabled">
              <Mic className="w-7 h-7 text-slate-300" />
            </Button>
          )}

          {!isActive && (
            <p className="text-sm text-slate-400 text-center">
              Simulation has ended. View your report for results.
            </p>
          )}

          {voiceState === "idle" && isActive && (
            <p className="text-xs text-slate-300">Tap the microphone to speak</p>
          )}
        </div>

        {isActive && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <Button variant="outline" size="sm" className="gap-1.5 border-primary/30 text-primary" onClick={() => setBriefingOpen(true)} data-testid="voice-briefing-btn">
              <Info className="w-4 h-4" />
              Briefing
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 border-slate-200 text-slate-500" onClick={() => setTranscriptOpen(true)} data-testid="transcript-btn">
                <FileText className="w-4 h-4" />
                Transcript
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5 border-slate-200 text-slate-500" onClick={onSwitchToText} data-testid="switch-to-text-btn">
                <Keyboard className="w-4 h-4" />
                Text Chat
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
